// "Live slides" (שקפים חיים): a tappable deck that replaces a chapter's old
// lecture slides. Each slide = picture inside a drawn head profile (the old
// slides' graphic idea, new palette), the slide's details as a choice the
// reader marks, and the full text folded under "עוד על השלב". Answers go
// through the same progress.fields map as the writing frames (FrameBox),
// so they sync across devices. Config per chapter: src/data/chNSlides.ts.
//
// Props: `slides` (the deck), `compact` (one slide, no navigation: used for
// the ten-step map on its own in the chapter's summary section).
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Slide } from "../data/ch4Slides";
import { CH4_MAP_STEPS } from "../data/ch4Slides";

type Props = {
  slides: Slide[];
  fields: Record<string, any>;
  setFields: (fn: any) => void;
  save?: React.ReactNode;
  compact?: boolean;
};

// The head is the original drawing from the old lecture slides
// (public/event.png): its outline strokes lifted as-is, recoloured navy,
// the inside filled white, the old text and circle removed, the neck fading
// out at the bottom (public/ch4/slides/head.webp, 674x693). The picture
// sits in the circle at (288,389) r188, where the old slides placed theirs.
const HEAD_IMG = "/ch4/slides/head.webp";

const RED = "#C62828";
const GREEN = "#2E7D32";
const GOLD = "#C9A44C";

function HeadArt({ img, alt, uid }: { img: string; alt: string; uid: string }) {
  const clip = `lsclip-${uid}`;
  return (
    <svg viewBox="0 0 674 693" className="lssvg" role="img" aria-label={alt}>
      <defs>
        <clipPath id={clip}>
          <circle cx="288" cy="389" r="188" />
        </clipPath>
      </defs>
      <image href={HEAD_IMG} x="0" y="0" width="674" height="693" />
      <circle cx="288" cy="389" r="193" fill="#fff" stroke={GOLD} strokeWidth="5" />
      <image href={img} x="100" y="201" width="376" height="376" clipPath={`url(#${clip})`} preserveAspectRatio="xMidYMid slice" />
    </svg>
  );
}

// The ten-step ring: 1-5 red (automatic), 6 gold (the bridge), 7-10 green
// (chosen). Steps up to the reader's mark are filled; the mark itself gets
// a gold halo. Tapping a step sets the mark (same key as frame 4.6).
function MapArt({ value, onPick }: { value: number; onPick: (n: number) => void }) {
  const cx = 200, cy = 200, r = 140;
  const pos = (n: number) => {
    // start at the top, go counter-clockwise (right-to-left reading)
    const a = (-90 - (n - 1) * 36) * (Math.PI / 180);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };
  const color = (n: number) => (n < 6 ? RED : n === 6 ? GOLD : GREEN);
  const step = CH4_MAP_STEPS.find((s) => s.n === value);
  return (
    <svg viewBox="0 0 400 400" className="lssvg" role="img" aria-label="תרשים עשרת השלבים">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--sand)" strokeWidth="3" />
      {/* the loop closes: step 10 feeds the next event */}
      <path d={`M ${pos(10).x - 4} ${pos(10).y - 18} Q ${cx + r + 10} ${cy - 40} ${pos(1).x + 26} ${pos(1).y - 2}`} fill="none" stroke="var(--sand)" strokeWidth="2" strokeDasharray="4 4" />
      {CH4_MAP_STEPS.map((s) => {
        const p = pos(s.n);
        const filled = value > 0 && s.n <= value;
        const mine = s.n === value;
        return (
          <g key={s.n} onClick={() => onPick(s.n)} style={{ cursor: "pointer" }}>
            {mine && <circle cx={p.x} cy={p.y} r={30} fill="none" stroke={GOLD} strokeWidth="4" />}
            <circle cx={p.x} cy={p.y} r={21} fill={filled ? color(s.n) : "#fff"} stroke={color(s.n)} strokeWidth="3" />
            <text x={p.x} y={p.y + 6} textAnchor="middle" fontSize="17" fontWeight="700" fontFamily="Heebo, Arial, sans-serif" fill={filled ? "#fff" : color(s.n)}>
              {s.n}
            </text>
          </g>
        );
      })}
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize="15" fontFamily="Heebo, Arial, sans-serif" fill="var(--muted)">
        {value > 0 ? `שלב ${value}` : "הבור שלי מגיע עד"}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize="17" fontWeight="700" fontFamily="Heebo, Arial, sans-serif" fill="var(--deep)">
        {step ? step.name : "הקישי על שלב"}
      </text>
      <text x={cx} y={cy + 40} textAnchor="middle" fontSize="12" fontFamily="Heebo, Arial, sans-serif" fill="var(--muted)">
        {value > 0 ? (value < 6 ? "לפני הגשר: אוטומטי" : value === 6 ? "הגשר: כאן הבחירה" : "אחרי הגשר: בחירה") : ""}
      </text>
    </svg>
  );
}

function Breath({ done, onDone }: { done: boolean; onDone: () => void }) {
  const [left, setLeft] = useState<number | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (left === null) return;
    if (left === 0) {
      onDoneRef.current();
      const t = setTimeout(() => setLeft(null), 1500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  if (left !== null) {
    return (
      <div className="lsbreath">
        <div className="big">{left === 0 ? "✓" : left}</div>
        <div className="lsq">{left === 0 ? "עברת את השש. זו כל ההשהיה." : left % 2 === 0 ? "שאפי עמוק..." : "ושחררי..."}</div>
      </div>
    );
  }
  return (
    <div className="lsbreath">
      <button type="button" onClick={() => setLeft(6)}>
        {done ? "עוד פעם: שש שניות" : "נסי עכשיו: שש שניות"}
      </button>
      {done && <div className="lshint">עשית את זה פעם אחת. בבור האמיתי זה בדיוק אותו כפתור, רק בלי מסך.</div>}
    </div>
  );
}

export default function LiveSlides({ slides, fields, setFields, save, compact }: Props) {
  const [cur, setCur] = useState(0);
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  const touchX = useRef<number | null>(null);
  const s = slides[cur];
  const val = (k: string) => String(fields[k] ?? "");
  const set = (k: string, v: string) => setFields((r: any) => ({ ...r, [k]: v }));
  const answered = (sl: Slide) => (sl.art === "recap" ? false : val(sl.choice?.key || sl.id) !== "");
  const go = (i: number) => setCur(Math.max(0, Math.min(slides.length - 1, i)));

  let body: React.ReactNode = null;
  if (s.choice) {
    const k = s.choice.key || s.id;
    const v = val(k);
    body = (
      <>
        <div className="lsq">{s.choice.q}</div>
        <div className="frchips">
          {s.choice.opts.map((o) => (
            <button type="button" key={o} className={v === o ? "on" : ""} onClick={() => set(k, v === o ? "" : o)}>
              {o}
            </button>
          ))}
        </div>
        <div className="lshint">{v && s.choice.hint[v] ? s.choice.hint[v] : "הקישי על אחת האפשרויות."}</div>
      </>
    );
  } else if (s.breath) {
    body = <Breath done={val(s.id) === "כן"} onDone={() => set(s.id, "כן")} />;
  } else if (s.art === "recap") {
    body = (
      <ul className="lsrecap">
        {slides
          .filter((x) => x.art !== "recap")
          .map((x) => {
            const k = x.choice?.key || x.id;
            const v = val(k);
            const txt = x.art === "map" && v ? `שלב ${v}: ${CH4_MAP_STEPS.find((m) => String(m.n) === v)?.name ?? ""}` : x.breath ? (v ? "עברתי שש שניות" : "") : v;
            return (
              <li key={x.id}>
                <b>{x.tag.replace(/^.*?:\s*/, "")}</b>
                {txt ? <span>{txt}</span> : <span className="none">עוד לא סימנת</span>}
              </li>
            );
          })}
      </ul>
    );
  }

  const art =
    s.art === "map" ? (
      <MapArt value={Number(val(s.id)) || 0} onPick={(n) => set(s.id, String(n))} />
    ) : s.art === "recap" ? null : s.img ? (
      <HeadArt img={s.img} alt={s.title} uid={`${uid}-${s.n}`} />
    ) : null;

  return (
    <div
      className="lsdeck"
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current === null || compact) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) < 50) return;
        // RTL: a swipe to the left moves forward
        go(dx < 0 ? cur + 1 : cur - 1);
      }}
    >
      <div className="lshead">
        <span>{s.tag}</span>
        {!compact && <span>{s.n} מתוך {slides.length}</span>}
      </div>
      {art && <div className="lsart">{art}</div>}
      <div className="lstitle">{s.title}</div>
      <p className="lslead">{s.lead}</p>
      {body}
      {s.more && (
        <details className="lsmore">
          <summary>עוד על השלב</summary>
          <div dangerouslySetInnerHTML={{ __html: s.more }} />
        </details>
      )}
      {!compact && (
        <div className="lsnav">
          <button type="button" className="ghost" disabled={cur === 0} onClick={() => go(cur - 1)}>
            → הקודם
          </button>
          <div className="lsdots">
            {slides.map((x, i) => (
              <button type="button" key={x.id} className={i === cur ? "on" : answered(x) ? "done" : ""} onClick={() => go(i)} aria-label={`שקף ${i + 1}`} />
            ))}
          </div>
          <button type="button" disabled={cur === slides.length - 1} onClick={() => go(cur + 1)}>
            {cur === slides.length - 1 ? "סוף" : "הבא ←"}
          </button>
        </div>
      )}
      {save}
    </div>
  );
}
